import { afterNextRender, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterModule, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import type {
  CarouselInterface,
  CarouselItem,
  CarouselOptions,
  InstanceOptions,
} from 'flowbite';
import { Carousel } from 'flowbite';
import { Observable, of } from 'rxjs';
import { PageService } from 'src/app/core/page/page.service';
import { RawFormJobObject } from 'src/app/data/dtos';
import { selectColorTheme } from 'src/app/store/color-theme/selectors';
import * as JobsActions from 'src/app/store/jobs/actions';
import { successJobsDataSelector } from 'src/app/store/jobs/selectors';
import { selectActiveLanguage } from 'src/app/store/language/selectors';
import { AppStateInterface } from 'src/app/store/types';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { NgOptimizedImage, NgClass, NgFor, AsyncPipe } from '@angular/common';
import { SafeHtmlPipe } from 'src/app/core/custom-pipes/safeHtml.pipe';
import { BrowserService } from 'src/app/core/browser.service';
import { bootstrapBriefcase, bootstrapCheckCircleFill, bootstrapGeoAltFill, bootstrapGlobe2 } from '@ng-icons/bootstrap-icons';
import { heroArrowRightSolid } from '@ng-icons/heroicons/solid';
import { Meta, Title } from '@angular/platform-browser';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    imports: [
        RouterLink,
        RouterModule,
        NgIcon,
        NgOptimizedImage,
        NgClass,
        NgFor,
        AsyncPipe,
        SafeHtmlPipe,
        TranslateModule,
    ],
    providers: [
        provideIcons({
            bootstrapCheckCircleFill,
            bootstrapGeoAltFill,
            bootstrapBriefcase,
            bootstrapGlobe2,
            heroArrowRightSolid
        })
    ]
})
export class HomeComponent {
  activeLanguage: string = 'en';
  activeTheme$: Observable<string>;
  private readonly companyLogo: string = 'company_logo';
  private readonly jobCategrories: string = 'job_categories';
  private readonly pageSize: number = 10;
  private readonly onlyFirstPage: number = 1;
  readonly baseCMSUrl: string = 'http://localhost:1337';
  openJobs$!: Observable<RawFormJobObject[]>;

  @ViewChild('silhouetteCarousel', { static: false })
  carouselElement!: ElementRef;
  @ViewChild('carouselItem1', { static: false }) carouselItem1!: ElementRef;
  @ViewChild('carouselItem2', { static: false }) carouselItem2!: ElementRef;
  @ViewChild('carouselItem3', { static: false }) carouselItem3!: ElementRef;
  @ViewChild('carouselItem4', { static: false }) carouselItem4!: ElementRef;
  @ViewChild('carouselItem5', { static: false }) carouselItem5!: ElementRef;
  @ViewChild('carouselItem6', { static: false }) carouselItem6!: ElementRef;
  @ViewChild('carouselItem7', { static: false }) carouselItem7!: ElementRef;

  @ViewChild('ItemOneDescription', { static: false })
  ItemOneDescription!: ElementRef;
  @ViewChild('ItemTwoDescription', { static: false })
  ItemTwoDescription!: ElementRef;
  @ViewChild('ItemThreeDescription', { static: false })
  ItemThreeDescription!: ElementRef;
  @ViewChild('ItemFourDescription', { static: false })
  ItemFourDescription!: ElementRef;
  @ViewChild('ItemFiveDescription', { static: false })
  ItemFiveDescription!: ElementRef;

  @ViewChild('OurServices', { static: false }) OurServicesSection!: ElementRef;

  playCheckmarkAnimation1: boolean = false;
  playCheckmarkAnimation2: boolean = false;
  playCheckmarkAnimation3: boolean = false;
  playCheckmarkAnimation4: boolean = false;
  playPlaneAnimation: boolean = false;

  constructor(
    private _pageService: PageService,
    private browserService: BrowserService,
    private _store: Store<AppStateInterface>,
    private _translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    private _router: Router,
    private titleService: Title,
    private metaService: Meta
  ) {
    this.activeTheme$ = this._store.select(selectColorTheme);
  }

  ngOnInit() {
    this.initSEOContent()
    this.activeLanguage = this._pageService.getActiveLanguage();
    this._translate.use(this.activeLanguage);
    this._store.select(selectActiveLanguage).subscribe({
      next: (activeLanguage) => {
        this.activeLanguage = activeLanguage;
        jobsQuery = this.createJobQuery(activeLanguage);

        this._store.dispatch(JobsActions.getJobsData({ jobsQuery: jobsQuery }));
        this._store.select(successJobsDataSelector).subscribe({
          next: (result) => {
            this.openJobs$ = of(result.data);
          },
          error: (error) => {
            console.log('ERROR: jobs were not loaded!');
            console.log(error);
          },
        });
      },
      error: (error) => {
        console.log('ERROR: Data was not loaded!');
        console.log(error);
      },
    });
    var jobsQuery = this.createJobQuery(this.activeLanguage ?? 'en');
  }

  ngAfterViewInit() {
    if(this.browserService.isBrowserPlatform()) {
      this.initcarouselElement(this.carouselElement);
      this.initTimelineIntersectionObserver();
      this._pageService.setDirection(this.activeLanguage);
    };
  }

  jobSelected(jobslug: string) {
    this.browserService.localStorageSetItem('selectedJob', jobslug);
  }

  private initcarouselElement(element: any): void {
    const items: CarouselItem[] = [
      {
        position: 0,
        el: this.carouselItem1.nativeElement,
      },
      {
        position: 1,
        el: this.carouselItem2.nativeElement,
      },
      {
        position: 2,
        el: this.carouselItem3.nativeElement,
      },
      {
        position: 3,
        el: this.carouselItem4.nativeElement,
      },
      {
        position: 4,
        el: this.carouselItem5.nativeElement,
      },
      {
        position: 5,
        el: this.carouselItem6.nativeElement,
      },
      {
        position: 6,
        el: this.carouselItem7.nativeElement,
      },
    ];

    // object options with default values
    const options: CarouselOptions = {
      defaultPosition: 1,
      interval: 10000,

      indicators: {
        activeClasses: 'bg-white dark:bg-gray-800',
        inactiveClasses: 'hover:bg-white dark:hover:bg-gray-800',
      },

      // callback functions
      onNext: () => {},
      onPrev: () => {},
      onChange: () => {},
    };

    // instance options object
    const instanceOptions: InstanceOptions = {
      id: 'silhouette-carousel',
      override: true,
    };

    const carousel: CarouselInterface = new Carousel(
      this.carouselElement.nativeElement,
      items,
      options,
      instanceOptions,
    );

    carousel.cycle();
  }

  private initTimelineIntersectionObserver() {
    const itemDescriptions = [
      this.ItemOneDescription.nativeElement,
      this.ItemTwoDescription.nativeElement,
      this.ItemThreeDescription.nativeElement,
      this.ItemFourDescription.nativeElement,
      this.ItemFiveDescription.nativeElement,
    ];
    const timelineIntersectionObserverOptions = {
      threshold: 0,
      rootMargin: '0% 0% -70% 0%',
    };
    const fadeInOnScroll = this.browserService.createIntersectionObserver(
      (itemDescriptions, fadeInOnScroll) => {
        itemDescriptions.forEach((itemDescription) => {
          if (!itemDescription.isIntersecting) {
            return;
          } else {
            switch (itemDescription.target.id) {
              case 'ItemOneDescription':
                this.playCheckmarkAnimation1 = true;
                break;
              case 'ItemTwoDescription':
                this.playCheckmarkAnimation2 = true;
                break;
              case 'ItemThreeDescription':
                this.playCheckmarkAnimation3 = true;
                break;
              case 'ItemFourDescription':
                this.playCheckmarkAnimation4 = true;
                break;
              case 'ItemFiveDescription':
                this.playPlaneAnimation = true;
                break;
            }
            // fade in animation is not necessary now
            // itemDescription.target.classList.add("animate-fade-in");
            fadeInOnScroll.unobserve(itemDescription.target);
          }
        });
      },
      timelineIntersectionObserverOptions,
    );
    itemDescriptions.forEach((itemDescription) => {
      fadeInOnScroll?.observe(itemDescription);
    });
  }

  private createJobQuery(activeLanguage: string): string {
    const pageQuery = `locale=${activeLanguage}&populate=${this.companyLogo}&populate=${this.jobCategrories}&pagination[page]=${this.onlyFirstPage}&pagination[pageSize]=${this.pageSize}&sort=createdAt:asc`;
    return pageQuery;
  }

  public initSEOContent() {
    const urlFragments = this._router.url.split('/');
    if (urlFragments && urlFragments.length > 1 && ['en', 'fa', 'de', 'id'].includes(urlFragments[1])) {
      const language = urlFragments[1];
      this.activatedRoute.data.subscribe(() => {
        // Common meta tags
        const imageUrl = 'https://almanjobs-strapi-public-bucket.s3-eu-central-2.ionoscloud.com/almanjobs_logo_primary_98d1b23517.svg';
        this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
        this.metaService.updateTag({ name: 'viewport', content: 'width=device-width, initial-scale=1.0' });
        this.metaService.updateTag({ name: 'author', content: 'Almanjobs' });
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'og:image', content: imageUrl });
        this.metaService.updateTag({ name: 'twitter:image', content: imageUrl });
        this.metaService.addTag({ rel: 'alternate', hreflang: 'x-default', href: `https://almanjobs.com/en`});

        // Language-specific meta tags
        if (language === 'en') {
          this.titleService.setTitle('Almanjobs - Find a Job, Apply for Visa, and Migrate to Germany');
          this.metaService.updateTag({
            name: 'description',
            content: 'Discover opportunities to work and live in Germany. We help skilled foreigners find jobs, secure job contracts, apply for visas, and integrate seamlessly into German life.',
          });
          this.metaService.updateTag({ name: 'keywords', content: 'find job in Germany, work in Germany, migrate to Germany, visa application, skilled workers, immigration services' });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'en', href: `https://almanjobs.com/en`});
        }

        if (language === 'de') {
          this.titleService.setTitle('Almanjobs - Job finden, Visum beantragen und nach Deutschland migrieren');
          this.metaService.updateTag({
            name: 'description',
            content: 'Entdecken Sie Möglichkeiten, in Deutschland zu arbeiten und zu leben. Wir helfen qualifizierten Fachkräften, Jobs zu finden, Arbeitsverträge abzuschließen, Visa zu beantragen und sich nahtlos in das deutsche Leben zu integrieren.',
          });
          this.metaService.updateTag({ name: 'keywords', content: 'Job in Deutschland finden, in Deutschland arbeiten, nach Deutschland migrieren, Visumantrag, Fachkräfte, Einwanderungsdienste' });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'de', href: `https://almanjobs.com/de`});
        }

        if (language === 'fa') {
          this.titleService.setTitle('Almanjobs - جستجوی شغل، درخواست ویزا و مهاجرت آسان به آلمان');
          this.metaService.updateTag({
            name: 'description',
            content: 'فرصت‌های کار و زندگی در آلمان را کشف کنید. ما به افراد ماهر کمک می‌کنیم تا شغل پیدا کنند، قرارداد کاری دریافت کنند، برای ویزا اقدام کنند و به آسانی در زندگی آلمان ادغام شوند.',
          });
          this.metaService.updateTag({ name: 'keywords', content: 'کار در آلمان، مهاجرت به آلمان، درخواست ویزا، خدمات مهاجرت، یافتن شغل در آلمان' });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'fa', href: `https://almanjobs.com/fa`});
        }
      });
    }
  }
}
